import React, { useMemo, useState, useCallback, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { getNextLoopOccurrence } from "../../utils/loopUtils";

const getFileName = (file) =>
  file.name ||
  file.file_name ||
  file.fileName ||
  file.original_filename ||
  "Attachment";

const getFileUrl = (file) =>
  file.url ||
  file.publicUrl ||
  file.public_url ||
  file.file_url ||
  file.download_url ||
  "";

const getFileKey = (file, index) =>
  file.id ||
  file.file_id ||
  file.path ||
  file.file_path ||
  getFileUrl(file) ||
  `${getFileName(file)}-${index}`;

const getFileCategory = (file) => {
  const name = getFileName(file).toLowerCase();
  const type = (file.type || file.mime_type || "").toLowerCase();

  if (
    type.startsWith("image/") ||
    /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(name)
  ) {
    return "Images";
  }

  if (/\.(xls|xlsx|csv)$/i.test(name)) {
    return "Sheets";
  }

  if (/\.(pdf|doc|docx|ppt|pptx|txt)$/i.test(name)) {
    return "Documents";
  }

  return "Documents";
};

function TaskDetailsPage({
  task,
  currentUser,
  users = [],
  teams = [],
  subTeams = [],
  onBack,
  onEdit,
  onUploadFile,
  onDeleteFiles,
  onAddExtension,
  onApproveExtension,
  onRejectExtension,
  onAddIssue,
  onAddUserCase,
  onAddAssignee,
  onDeleteAssignees,
  onDeleteTask,
  onAdvanceIssue,
  onAdvanceUserCase,
  initialTab = "overview",
  darkMode = false
}) {
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  const [fileSearch, setFileSearch] = useState("");
  const [extensionForm, setExtensionForm] = useState({
    requestedDueDate: "",
    reason: "",
    requestNote: "",
    files: [],
  });
  const [showUserCaseForm, setShowUserCaseForm] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [selectedUserCase, setSelectedUserCase] = useState(null);
  const [showAssigneeForm, setShowAssigneeForm] = useState(false);
  const [newAssignee, setNewAssignee] = useState({
    name: "",
    email: "",
    id: "",
  });
  const [showTaskMenu, setShowTaskMenu] = useState(false);
  const [showFileUploadForm, setShowFileUploadForm] = useState(false);
  const [fileUploadForm, setFileUploadForm] = useState({
    fileName: "",
    file: null,
  });
  const [localFiles, setLocalFiles] = useState([]);
  const [selectedFileKeys, setSelectedFileKeys] = useState([]);
  const [fileTypeFilter, setFileTypeFilter] = useState("All");
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [parentLoopData, setParentLoopData] = useState(null);

  useEffect(() => {
    if (task?.is_loop_occurrence && task?.loop_series_id) {
      const fetchParent = async () => {
        try {
          const { data } = await supabase.from('tasks').select('*').eq('id', task.loop_series_id).single();
          if (data) setParentLoopData(data);
        } catch (err) {
          console.error("Failed to fetch parent loop data", err);
        }
      };
      fetchParent();
    }
  }, [task]);

  const [actionLoading, setActionLoading] = useState({
    issue: false,
    assignee: false,
    userStory: false,
    extension: false,
    files: false
  });
  const getRequestedByName = (request) => {
    const user =
      users.find((u) => u.id === request?.requested_by) ||
      request?.requester ||
      request?.profiles;

    return user?.name || user?.email || "Unknown User";
  };

  const handleAssigneeFieldChange = (field, value) => {
    const matchedUser = users.find((u) => {
      const userName = (u.name || "").toLowerCase();
      const userEmail = (u.email || "").toLowerCase();
      const inputValue = (value || "").toLowerCase();

      return userName === inputValue || userEmail === inputValue;
    });

    if (matchedUser) {
      setNewAssignee({
        name: matchedUser.name || "",
        email: matchedUser.email || "",
        id: matchedUser.id || "",
      });
      return;
    }

    setNewAssignee((prev) => ({
      ...prev,
      [field]: value,
      id: "",
    }));
  };

  const [userCaseForm, setUserCaseForm] = useState({
    title: "",
    description: "",
    acceptanceCriteria: "",
    testCases: [""],
    files: [],
  });

  const [issueForm, setIssueForm] = useState({
    title: "",
    description: "",
    priority: "Medium",
    files: [],
  });



  const getAllTaskFiles = useCallback(() => {
    const allFiles = [];

    if (Array.isArray(task?.attachments)) {
      allFiles.push(...task.attachments);
    }

    if (Array.isArray(task?.task_files)) {
      allFiles.push(...task.task_files);
    }

    if (Array.isArray(task?.task_issues)) {
      task.task_issues.forEach((issue) => {
        if (Array.isArray(issue.attachments)) {
          allFiles.push(
            ...issue.attachments.map((file) => ({
              ...file,
              source: "Issue",
              sourceTitle: issue.title,
            }))
          );
        }
      });
    }

    if (Array.isArray(task?.task_extension_requests)) {
      task.task_extension_requests.forEach((request) => {
        if (Array.isArray(request.attachments)) {
          allFiles.push(
            ...request.attachments.map((file) => ({
              ...file,
              source: "Request Extension",
            }))
          );
        }
      });
    }

    if (Array.isArray(task?.task_user_cases)) {
      task.task_user_cases.forEach((userStory) => {
        if (Array.isArray(userStory.attachments)) {
          allFiles.push(
            ...userStory.attachments.map((file) => ({
              ...file,
              source: "User Story",
              sourceTitle: userStory.title,
            }))
          );
        }
      });
    }

    return allFiles;
  }, [task]);

  const attachments = useMemo(() => {
    return [...getAllTaskFiles(), ...localFiles];
  }, [localFiles, getAllTaskFiles]);
  const userCases = useMemo(() => {
    if (Array.isArray(task?.task_user_cases)) return task.task_user_cases;
    if (Array.isArray(task?.user_cases)) return task.user_cases;
    return [];
  }, [task]);

  const extensionRequests = useMemo(() => {
    const requests = Array.isArray(task?.task_extension_requests)
      ? task.task_extension_requests
      : [];

    return requests.filter((request) => request.status !== "Rejected");
  }, [task]);

  const issues = useMemo(() => {
    if (Array.isArray(task?.task_issues)) return task.task_issues;
    if (Array.isArray(task?.issues)) return task.issues;
    return [];
  }, [task]);

  const openIssues = issues.filter((issue) => issue.status === "Open");
  const inProgressIssues = issues.filter((issue) => issue.status === "In Progress");
  const resolvedIssues = issues.filter((issue) => issue.status === "Resolved");

  const pendingUserCases = userCases.filter(
    (uc) => (uc.status || "Pending") === "Pending"
  );
  const passedUserCases = userCases.filter((uc) => uc.status === "Passed");
  const inReviewUserCases = userCases.filter((uc) => uc.status === "In Review");


  const notes = useMemo(() => {
    return Array.isArray(task?.task_notes) ? task.task_notes : [];
  }, [task]);

  const assignees = useMemo(() => {
    if (!Array.isArray(task?.task_assignees)) return [];

    return task.task_assignees
      .map((a) => {
        const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles;
        const matchedUser = users.find((u) => u.id === a.user_id);
        const finalUser = profile || matchedUser;

        if (!finalUser) return null;

        return {
          id: finalUser.id || a.user_id,
          name: finalUser.name || "",
          email: finalUser.email || "",
          role: finalUser.role || "",
          photo_url: finalUser.photo_url || "",
        };
      })
      .filter(Boolean);
  }, [task, users]);


  const renderAssigneesValue = () => {
    if (!assignees.length) return "Unassigned";

    const names = assignees
      .map((a) => a.name || a.email)
      .filter(Boolean);

    let visibleNames = names;
    let remaining = 0;

    if (names.length > 4) {
      visibleNames = names.slice(0, 3);
      remaining = names.length - 3;
    }

    return (
      <div
        className="summary-assignees"
        title={names.join("\n")}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          lineHeight: 1.2,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {visibleNames.map((name, i) => (
          <div key={i} style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {name}
          </div>
        ))}
        {remaining > 0 && (
          <div
            style={{
              color: "#64748b",
              fontSize: "12px",
              fontWeight: 500,
              marginTop: "2px"
            }}
          >
            +{remaining} more
          </div>
        )}
      </div>
    );
  };

  const getCreatedByName = (t) => {
    const creator = users.find((u) => u.id === t?.created_by);
    return creator?.name || creator?.email || "—";
  };



  const filteredFiles = useMemo(() => {
    return attachments.filter((file) => {
      const fileName = getFileName(file).toLowerCase();

      const matchesSearch = fileSearch.trim()
        ? fileName.includes(fileSearch.toLowerCase())
        : true;

      const matchesType =
        fileTypeFilter === "All"
          ? true
          : getFileCategory(file) === fileTypeFilter;

      return matchesSearch && matchesType;
    });
  }, [attachments, fileSearch, fileTypeFilter]);

  const completionStats = useMemo(() => {
    const total = userCases.length || 1;
    const completed = userCases.filter(
      (uc) => uc.status === "Passed" || uc.status === "Completed"
    ).length;
    const percent = Math.round((completed / total) * 100);

    return {
      total,
      completed,
      inProgress: userCases.filter((uc) => uc.status === "In Review").length,
      todo: total - completed,
      percent
    };
  }, [userCases]);

  const pageBg = darkMode ? "#111827" : "#f8fafc";
  const cardBg = darkMode ? "#1f2937" : "#ffffff";
  const border = darkMode ? "1px solid #374151" : "1px solid #e5e7eb";
  const textMain = darkMode ? "#f9fafb" : "#111827";
  const textMuted = darkMode ? "#9ca3af" : "#64748b";

  const canDeleteAssignees = [
    "Global Admin",
    "Operational Admin",
    "Department Admin",
  ].includes(currentUser?.role);

  const canDeleteTask = canDeleteAssignees;

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "files", label: "Files", count: attachments.length },
    { key: "extension", label: "Request Extension", count: extensionRequests.length },
    { key: "issues", label: "Issues", count: issues.length },
    { key: "userCases", label: "User Stories", count: userCases.length }
  ];

  const formatDate = (date) => {
    if (!date) return "-";

    const cleanDate = String(date).slice(0, 10);
    const [year, month, day] = cleanDate.split("-").map(Number);

    if (!year || !month || !day) return cleanDate;

    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const loopMetadata = parentLoopData || task;

  const getLoopDurationText = () => {
    if (loopMetadata?.task_type !== "loop") return "-";

    const value = loopMetadata?.loop_duration_value || 1;
    const unit = loopMetadata?.loop_duration_unit === "weeks" ? "week(s)" : "day(s)";

    return `${value} ${unit}`;
  };

  const getRepeatText = () => {
    if (loopMetadata?.task_type !== "loop") return "Regular Task";

    return `${loopMetadata?.repeat_frequency || "Daily"} / Every ${loopMetadata?.repeat_every || 1}`;
  };

  const getNextOccurrenceDate = () => {
    return getNextLoopOccurrence(loopMetadata, task);
  };




  const getIssueNumber = (issue) => {
    if (issue.issue_number) return issue.issue_number;
    if (issue.issue_no) return issue.issue_no;

    const originalIndex = issues.findIndex((item) => item.id === issue.id);
    return `ISSUE_${String(originalIndex + 1).padStart(3, "0")}`;
  };

  const getUserStoryNumber = (userStory) => {
    if (userStory.user_story_number) return userStory.user_story_number;
    if (userStory.user_case_number) return userStory.user_case_number;
    if (userStory.user_case_no) return userStory.user_case_no;

    const originalIndex = userCases.findIndex((item) => item.id === userStory.id);
    return `US-${String(originalIndex + 1).padStart(3, "0")}`;
  };

  const renderOverview = () => {
    if (task?.task_type === "loop") {
      return (
        <div
          className="task-details-overview-grid loop-task"
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1.1fr 1.1fr 1fr",
            gap: "18px",
          }}
        >
          {/* Column 1: Description + User Stories */}
          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                textMain={textMain}
                title="Description"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
              <p style={{ color: textMain, fontSize: "14px", lineHeight: 1.6, marginTop: 0, fontWeight: 500 }}>
                {task?.description || "No description added."}
              </p>

              {task?.task_note && (
                <>
                  <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>Task Note</h4>
                  <p style={{ color: textMain, fontSize: "14px", fontWeight: 500, lineHeight: 1.6, marginTop: 0 }}>{task.task_note}</p>
                </>
              )}

              {notes.length > 0 && (
                <>
                  <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>Notes</h4>
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      style={{
                        padding: "10px 0",
                        borderTop: darkMode ? "1px solid #374151" : "1px solid #f1f5f9"
                      }}
                    >
                      <p style={{ margin: 0, color: textMuted, fontSize: "13px" }}>{note.note}</p>
                    </div>
                  ))}
                </>
              )}

              <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>
                Acceptance Criteria
              </h4>

              {userCases.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {userCases.map((uc, index) => (
                    <div
                      key={uc.id || index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "20px 1fr",
                        columnGap: "10px",
                        alignItems: "start",
                        padding: "10px 0",
                        borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
                      }}
                    >
                      <input id="statusCompleted" name="statusCompleted"
                        type="checkbox"
                        checked={uc.status === "Passed" || uc.status === "Completed"}
                        readOnly
                        style={{
                          marginTop: "3px",
                          width: "14px",
                          height: "14px",
                        }}
                      />

                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            color: textMain,
                            fontWeight: 600,
                            fontSize: "14px",
                            lineHeight: 1.4,
                            marginBottom: "4px",
                            wordBreak: "break-word",
                          }}
                        >
                          {uc.title || `User Story ${index + 1}`}
                        </div>

                        <div
                          style={{
                            color: textMuted,
                            fontSize: "13px",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {uc.acceptance_criteria ||
                            uc.acceptanceCriteria ||
                            "No acceptance criteria added."}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: textMuted, fontSize: "13px" }}>No acceptance criteria added.</p>
              )}
            </Card>

            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                title="User Stories"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                }
                action={
                  <button
                    onClick={() => {
                      setActiveTab("userCases");
                      setShowUserCaseForm(true);
                    }}
                    style={smallButtonStyle}
                  >
                    + User Story
                  </button>
                }
              />
              <UserCaseMiniList
                userCases={userCases}
                textMain={textMain}
                textMuted={textMuted}
                getUserStoryNumber={getUserStoryNumber}
                onOpenUserCase={(userStory) => {
                  setSelectedUserCase(userStory);
                  setActiveTab("userCases");
                }}
              />
            </Card>
          </div>

          {/* Column 2: Request Extension + Issues */}
          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                title="Request Extension"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                action={
                  <button onClick={() => setActiveTab("extension")} style={smallButtonStyle}>
                    + Request
                  </button>
                }
              />
              <ExtensionCard
                requests={extensionRequests}
                task={task}
                users={users}
                textMuted={textMuted}
                textMain={textMain}
                formatDate={formatDate}
                currentUser={currentUser}
                onApproveExtension={onApproveExtension}
                onRejectExtension={onRejectExtension}
                onOpenExtension={(request, extensionNumber) => {
                  setSelectedExtension({
                    ...request,
                    extension_number: extensionNumber,
                  });
                  setActiveTab("extension");
                }}
              />
            </Card>

            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                title="Issues"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                }
                action={
                  <button
                    onClick={() => {
                      setActiveTab("issues");
                      setShowIssueForm(true);
                    }}
                    style={smallButtonStyle}
                  >
                    + Issue
                  </button>
                }
              />
              <IssueList
                issues={issues}
                task={task}
                textMuted={textMuted}
                textMain={textMain}
                onOpenIssue={(issue) => {
                  setSelectedIssue(issue);
                  setActiveTab("issues");
                }}
              />
            </Card>
          </div>

          {/* Column 3: Loop Schedule + Task Completion */}
          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                textMain={textMain}
                title="Loop Schedule"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
              />
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  ["Repeat Frequency", loopMetadata?.repeat_frequency || "-"],
                  ["Every", `${loopMetadata?.repeat_every || 1} ${loopMetadata?.repeat_frequency === "Weekly" ? "week" : "day"}${Number(loopMetadata?.repeat_every) > 1 ? "s" : ""}`],
                  ["Occurrences", loopMetadata?.repeat_occurrences || "-"],
                  ["Task Duration", getLoopDurationText()],
                  ["Start Date", formatDate(loopMetadata?.repeat_start_date || loopMetadata?.start_date)],
                  ["End Date", formatDate(loopMetadata?.repeat_end_date || "-")],
                  ...(task?.is_loop_occurrence ? [
                    ["Occurrence Number", task?.occurrence_index !== undefined ? task.occurrence_index + 1 : (task?.occurrence_number || "-")]
                  ] : [])
                ].map(([lbl, val]) => (
                  <div
                    key={lbl}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "13px",
                      paddingBottom: "8px",
                      borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9"
                    }}
                  >
                    <span style={{ color: "#64748b", fontWeight: 400 }}>{lbl}</span>
                    <span style={{ color: textMain, fontWeight: 500 }}>{val}</span>
                  </div>
                ))}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "13px",
                    marginTop: "6px",
                    padding: "10px 12px",
                    background: darkMode ? "rgba(22, 163, 74, 0.1)" : "#f0fdf4",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ color: "#16a34a", fontWeight: 600 }}>Next Occurrence</span>
                  <span style={{ color: "#16a34a", fontWeight: 600 }}>
                    {getNextOccurrenceDate() || "-"}
                  </span>
                </div>
              </div>
            </Card>

            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                textMain={textMain}
                title="Task Completion"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
              <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
                <div
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    background: `conic-gradient(#2563eb ${completionStats.percent}%, #e5e7eb 0)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "50%",
                      background: cardBg,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <strong style={{ fontSize: "20px", color: "#2563eb", fontWeight: 600 }}>
                      {completionStats.percent}%
                    </strong>
                    <span style={{ fontSize: "10px", fontWeight: 500, color: textMuted }}>
                      Overall
                    </span>
                  </div>
                </div>

                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                  {[
                    ["Completed", completionStats.completed],
                    ["In Progress", completionStats.inProgress],
                    ["To Do", completionStats.todo]
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "12px",
                          fontWeight: 500,
                          color: textMain
                        }}
                      >
                        <span>{label}</span>
                        <span>
                          {value}/{completionStats.total}
                        </span>
                      </div>
                      <div
                        style={{
                          height: "6px",
                          background: "#e5e7eb",
                          borderRadius: "999px",
                          marginTop: "4px"
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min((value / completionStats.total) * 100, 100)}%`,
                            height: "6px",
                            background: label === "Completed" ? "#76AB3F" : "#2563eb",
                            borderRadius: "999px"
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Column 4: Assigned Users + Files */}
          <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                textMain={textMain}
                title="Assigned Users"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                }
                action={
                  <button
                    onClick={() => setShowAssigneeForm(true)}
                    style={smallButtonStyle}
                  >
                    +
                  </button>
                }
              />
              {showAssigneeForm && (
                <div className="task-details-assignee-form-grid" style={{ marginBottom: "14px" }}>
                  <fieldset disabled={actionLoading.assignee} style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "12px" }}>
                    <input id="name" name="name"
                      value={newAssignee.name}
                      onChange={(e) => handleAssigneeFieldChange("name", e.target.value)}
                      list="task-details-assignee-list"
                      placeholder="Assignee name"
                      autoComplete="off"
                      style={{
                        border,
                        borderRadius: "10px",
                        padding: "8px 12px",
                        fontSize: "13px",
                      }}
                    />

                    <input id="email" name="email"
                      value={newAssignee.email}
                      onChange={(e) => handleAssigneeFieldChange("email", e.target.value)}
                      list="task-details-assignee-email-list"
                      placeholder="Assignee email"
                      autoComplete="off"
                      style={{
                        border,
                        borderRadius: "10px",
                        padding: "8px 12px",
                        fontSize: "13px",
                      }}
                    />

                    <datalist id="task-details-assignee-list">
                      {users.map((u) => (
                        <option key={`name-${u.id}`} value={u.name || u.email} />
                      ))}
                    </datalist>

                    <datalist id="task-details-assignee-email-list">
                      {users.map((u) => (
                        <option key={`email-${u.id}`} value={u.email || u.name} />
                      ))}
                    </datalist>

                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        disabled={actionLoading.assignee}
                        onClick={async () => {
                          if (actionLoading.assignee) return;
                          const selectedUser =
                            newAssignee.id
                              ? newAssignee
                              : users.find(
                                (u) =>
                                  (u.name || "").toLowerCase() ===
                                  (newAssignee.name || "").toLowerCase() ||
                                  (u.email || "").toLowerCase() ===
                                  (newAssignee.email || "").toLowerCase()
                              );

                          if (!selectedUser?.id) {
                            alert("Please select a valid user from the list.");
                            return;
                          }

                          setActionLoading(prev => ({ ...prev, assignee: true }));
                          try {
                            const success = await onAddAssignee(selectedUser);

                            if (success) {
                              setNewAssignee({ name: "", email: "", id: "" });
                              setShowAssigneeForm(false);
                            }
                          } catch (err) {
                            alert("Failed to add assignee: " + (err.message || "Unknown error"));
                          } finally {
                            setActionLoading(prev => ({ ...prev, assignee: false }));
                          }
                        }}
                        style={{
                          ...smallButtonStyle,
                          background: "#76AB3F",
                          color: "white",
                          opacity: actionLoading.assignee ? 0.6 : 1,
                          cursor: actionLoading.assignee ? "not-allowed" : "pointer"
                        }}
                      >
                        {actionLoading.assignee ? "Adding assignee..." : "Add"}
                      </button>
                      <button
                        disabled={actionLoading.assignee}
                        onClick={() => setShowAssigneeForm(false)}
                        style={{
                          ...smallButtonStyle,
                          background: "#e5e7eb",
                          color: "#374151",
                          opacity: actionLoading.assignee ? 0.6 : 1,
                          cursor: actionLoading.assignee ? "not-allowed" : "pointer"
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </fieldset>
                </div>
              )}

              {selectedAssigneeIds.length > 0 && canDeleteAssignees && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "10px",
                    padding: "10px 12px",
                    marginBottom: "12px",
                  }}
                >
                  <strong style={{ color: "#991b1b", fontSize: "13px", fontWeight: 600 }}>
                    {selectedAssigneeIds.length} user(s)
                  </strong>

                  <button
                    onClick={async () => {
                      if (!window.confirm("Delete selected user(s) from this task?")) return;

                      const deletingAll = selectedAssigneeIds.length === assignees.length;

                      if (deletingAll) {
                        if (!window.confirm("All users are selected. This will delete the task. Continue?")) return;

                        if (onDeleteTask) {
                          const success = await onDeleteTask(task?.id);

                          if (success) {
                            onBack && onBack();
                          } else {
                            alert("Failed to delete task.");
                          }
                        }

                        return;
                      }

                      if (onDeleteAssignees) {
                        const success = await onDeleteAssignees({
                          taskId: task?.id,
                          userIds: selectedAssigneeIds,
                        });

                        if (!success) {
                          alert("Failed to delete selected user(s).");
                          return;
                        }
                      }

                      setSelectedAssigneeIds([]);
                    }}
                    style={{
                      border: "none",
                      background: "#dc2626",
                      color: "white",
                      borderRadius: "8px",
                      padding: "7px 12px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}

              {assignees.length > 0 ? (
                assignees.map((user, index) => {
                  const userKey = user.id || user.email || user.name;

                  return (
                    <div
                      key={userKey || index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: canDeleteAssignees ? "28px 34px 1fr" : "34px 1fr",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 0",
                        borderBottom:
                          index !== assignees.length - 1
                            ? darkMode
                              ? "1px solid #374151"
                              : "1px solid #f1f5f9"
                            : "none",
                      }}
                    >
                      {canDeleteAssignees && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAssigneeIds((prev) =>
                              prev.includes(userKey)
                                ? prev.filter((id) => id !== userKey)
                                : [...prev, userKey]
                            );
                          }}
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            border: selectedAssigneeIds.includes(userKey)
                              ? "6px solid #2563eb"
                              : "2px solid #cbd5e1",
                            background: "#ffffff",
                            cursor: "pointer",
                          }}
                        />
                      )}

                      <Avatar name={user.name || user.email} />

                      <div>
                        <div style={{ fontWeight: 600, color: textMain, fontSize: "14px" }}>
                          {user.name || "-"}
                        </div>
                        <div style={{ fontSize: "12px", color: textMuted }}>
                          {user.email || "-"}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ color: textMuted, fontSize: "13px" }}>No assigned users found.</p>
              )}
            </Card>

            <Card cardBg={cardBg} border={border} darkMode={darkMode}>
              <SectionTitle
                title="Files"
                icon={
                  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                }
                action={
                  <button
                    onClick={() => {
                      setActiveTab("files");
                    }}
                    style={smallButtonStyle}
                  >
                    + Upload
                  </button>
                }
              />
              {attachments.slice(0, 3).map((file, index) => (
                <FileRow
                  key={index}
                  file={file}
                  textMain={textMain}
                  textMuted={textMuted}
                  selectable={false}
                />
              ))}
              <button
                onClick={() => setActiveTab("files")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontWeight: 600,
                  marginTop: "10px",
                  cursor: "pointer"
                }}
              >
                View all files
              </button>
            </Card>
          </div>
        </div>
      );
    }

    // regular task layout
    return (
      <div
        className="task-details-overview-grid regular-task"
        style={{
          display: "grid",
          gridTemplateColumns: "1.25fr 1.15fr 1.1fr",
          gap: "18px",
        }}
      >
        {/* Column 1: Description + User Stories */}
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              textMain={textMain}
              title="Description"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />
            <p style={{ color: textMain, fontSize: "14px", lineHeight: 1.6, marginTop: 0, fontWeight: 500 }}>
              {task?.description || "No description added."}
            </p>

            {task?.task_note && (
              <>
                <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>Task Note</h4>
                <p style={{ color: textMain, fontSize: "14px", fontWeight: 500, lineHeight: 1.6, marginTop: 0 }}>{task.task_note}</p>
              </>
            )}

            {notes.length > 0 && (
              <>
                <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>Notes</h4>
                {notes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      padding: "10px 0",
                      borderTop: darkMode ? "1px solid #374151" : "1px solid #f1f5f9"
                    }}
                  >
                    <p style={{ margin: 0, color: textMuted, fontSize: "13px" }}>{note.note}</p>
                  </div>
                ))}
              </>
            )}

            <h4 style={{ color: textMuted, fontSize: "12px", fontWeight: 600, marginTop: "18px", marginBottom: "8px" }}>
              Acceptance Criteria
            </h4>

            {userCases.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {userCases.map((uc, index) => (
                  <div
                    key={uc.id || index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "20px 1fr",
                      columnGap: "10px",
                      alignItems: "start",
                      padding: "10px 0",
                      borderBottom: darkMode ? "1px solid #374151" : "1px solid #f1f5f9",
                    }}
                  >
                    <input id="statusCompleted" name="statusCompleted"
                      type="checkbox"
                      checked={uc.status === "Passed" || uc.status === "Completed"}
                      readOnly
                      style={{
                        marginTop: "3px",
                        width: "14px",
                        height: "14px",
                      }}
                    />

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          color: textMain,
                          fontWeight: 600,
                          fontSize: "14px",
                          lineHeight: 1.4,
                          marginBottom: "4px",
                          wordBreak: "break-word",
                        }}
                      >
                        {uc.title || `User Story ${index + 1}`}
                      </div>

                      <div
                        style={{
                          color: textMuted,
                          fontSize: "13px",
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {uc.acceptance_criteria ||
                          uc.acceptanceCriteria ||
                          "No acceptance criteria added."}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: textMuted, fontSize: "13px" }}>No acceptance criteria added.</p>
            )}
          </Card>

          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              title="User Stories"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
              action={
                <button
                  onClick={() => {
                    setActiveTab("userCases");
                    setShowUserCaseForm(true);
                  }}
                  style={smallButtonStyle}
                >
                  + User Story
                </button>
              }
            />
            <UserCaseMiniList
              userCases={userCases}
              textMain={textMain}
              textMuted={textMuted}
              getUserStoryNumber={getUserStoryNumber}
              onOpenUserCase={(userStory) => {
                setSelectedUserCase(userStory);
                setActiveTab("userCases");
              }}
            />
          </Card>
        </div>

        {/* Column 2: Request Extension + Issues */}
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              title="Request Extension"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              action={
                <button onClick={() => setActiveTab("extension")} style={smallButtonStyle}>
                  + Request
                </button>
              }
            />
            <ExtensionCard
              requests={extensionRequests}
              task={task}
              users={users}
              textMuted={textMuted}
              textMain={textMain}
              formatDate={formatDate}
              currentUser={currentUser}
              onApproveExtension={onApproveExtension}
              onRejectExtension={onRejectExtension}
              onOpenExtension={(request, extensionNumber) => {
                setSelectedExtension({
                  ...request,
                  extension_number: extensionNumber,
                });
                setActiveTab("extension");
              }}
            />
          </Card>

          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              title="Issues"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              }
              action={
                <button
                  onClick={() => {
                    setActiveTab("issues");
                    setShowIssueForm(true);
                  }}
                  style={smallButtonStyle}
                >
                  + Issue
                </button>
              }
            />
            <IssueList
              issues={issues}
              task={task}
              textMuted={textMuted}
              textMain={textMain}
              onOpenIssue={(issue) => {
                setSelectedIssue(issue);
                setActiveTab("issues");
              }}
            />
          </Card>
        </div>

        {/* Column 3: Task Completion + Assigned Users + Files */}
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              textMain={textMain}
              title="Task Completion"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
            />
            <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  background: `conic-gradient(#2563eb ${completionStats.percent}%, #e5e7eb 0)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "50%",
                    background: cardBg,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <strong style={{ fontSize: "20px", color: "#2563eb", fontWeight: 600 }}>
                    {completionStats.percent}%
                  </strong>
                  <span style={{ fontSize: "10px", fontWeight: 500, color: textMuted }}>
                    Overall
                  </span>
                </div>
              </div>

              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  ["Completed", completionStats.completed],
                  ["In Progress", completionStats.inProgress],
                  ["To Do", completionStats.todo]
                ].map(([label, value]) => (
                  <div key={label}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: textMain
                      }}
                    >
                      <span>{label}</span>
                      <span>
                        {value}/{completionStats.total}
                      </span>
                    </div>
                    <div
                      style={{
                        height: "6px",
                        background: "#e5e7eb",
                        borderRadius: "999px",
                        marginTop: "4px"
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min((value / completionStats.total) * 100, 100)}%`,
                          height: "6px",
                          background: label === "Completed" ? "#76AB3F" : "#2563eb",
                          borderRadius: "999px"
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              textMain={textMain}
              title="Assigned Users"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              }
              action={
                <button
                  onClick={() => setShowAssigneeForm(true)}
                  style={smallButtonStyle}
                >
                  +
                </button>
              }
            />
            {showAssigneeForm && (
              <div style={{ marginBottom: "14px" }}>
                <fieldset disabled={actionLoading.assignee} style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "12px" }}>
                  <input id="name" name="name"
                    value={newAssignee.name}
                    onChange={(e) => handleAssigneeFieldChange("name", e.target.value)}
                    list="task-details-assignee-list"
                    placeholder="Assignee name"
                    autoComplete="off"
                    style={{
                      border,
                      borderRadius: "10px",
                      padding: "8px 12px",
                      fontSize: "13px",
                    }}
                  />

                  <input id="email" name="email"
                    value={newAssignee.email}
                    onChange={(e) => handleAssigneeFieldChange("email", e.target.value)}
                    list="task-details-assignee-email-list"
                    placeholder="Assignee email"
                    autoComplete="off"
                    style={{
                      border,
                      borderRadius: "10px",
                      padding: "8px 12px",
                      fontSize: "13px",
                    }}
                  />

                  <datalist id="task-details-assignee-list">
                    {users.map((u) => (
                      <option key={`name-${u.id}`} value={u.name || u.email} />
                    ))}
                  </datalist>

                  <datalist id="task-details-assignee-email-list">
                    {users.map((u) => (
                      <option key={`email-${u.id}`} value={u.email || u.name} />
                    ))}
                  </datalist>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      disabled={actionLoading.assignee}
                      onClick={async () => {
                        if (actionLoading.assignee) return;
                        const selectedUser =
                          newAssignee.id
                            ? newAssignee
                            : users.find(
                              (u) =>
                                (u.name || "").toLowerCase() ===
                                (newAssignee.name || "").toLowerCase() ||
                                (u.email || "").toLowerCase() ===
                                (newAssignee.email || "").toLowerCase()
                            );

                        if (!selectedUser?.id) {
                          alert("Please select a valid user from the list.");
                          return;
                        }

                        setActionLoading(prev => ({ ...prev, assignee: true }));
                        try {
                          const success = await onAddAssignee(selectedUser);

                          if (success) {
                            setNewAssignee({ name: "", email: "", id: "" });
                            setShowAssigneeForm(false);
                          }
                        } catch (err) {
                          alert("Failed to add assignee: " + (err.message || "Unknown error"));
                        } finally {
                          setActionLoading(prev => ({ ...prev, assignee: false }));
                        }
                      }}
                      style={{
                        ...smallButtonStyle,
                        background: "#76AB3F",
                        color: "white",
                        opacity: actionLoading.assignee ? 0.6 : 1,
                        cursor: actionLoading.assignee ? "not-allowed" : "pointer"
                      }}
                    >
                      {actionLoading.assignee ? "Adding assignee..." : "Add"}
                    </button>
                    <button
                      disabled={actionLoading.assignee}
                      onClick={() => setShowAssigneeForm(false)}
                      style={{
                        ...smallButtonStyle,
                        background: "#e5e7eb",
                        color: "#374151",
                        opacity: actionLoading.assignee ? 0.6 : 1,
                        cursor: actionLoading.assignee ? "not-allowed" : "pointer"
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </fieldset>
              </div>
            )}

            {selectedAssigneeIds.length > 0 && canDeleteAssignees && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "10px",
                  padding: "10px 12px",
                  marginBottom: "12px",
                }}
              >
                <strong style={{ color: "#991b1b", fontSize: "13px", fontWeight: 600 }}>
                  {selectedAssigneeIds.length} user(s)
                </strong>

                <button
                  onClick={async () => {
                    if (!window.confirm("Delete selected user(s) from this task?")) return;

                    const deletingAll = selectedAssigneeIds.length === assignees.length;

                    if (deletingAll) {
                      if (!window.confirm("All users are selected. This will delete the task. Continue?")) return;

                      if (onDeleteTask) {
                        const success = await onDeleteTask(task?.id);

                        if (success) {
                          onBack && onBack();
                        } else {
                          alert("Failed to delete task.");
                        }
                      }

                      return;
                    }

                    if (onDeleteAssignees) {
                      const success = await onDeleteAssignees({
                        taskId: task?.id,
                        userIds: selectedAssigneeIds,
                      });

                      if (!success) {
                        alert("Failed to delete selected user(s).");
                        return;
                      }
                    }

                    setSelectedAssigneeIds([]);
                  }}
                  style={{
                    border: "none",
                    background: "#dc2626",
                    color: "white",
                    borderRadius: "8px",
                    padding: "7px 12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            )}

            {assignees.length > 0 ? (
              assignees.map((user, index) => {
                const userKey = user.id || user.email || user.name;

                return (
                  <div
                    key={userKey || index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: canDeleteAssignees ? "28px 34px 1fr" : "34px 1fr",
                      alignItems: "center",
                      gap: "12px",
                      padding: "10px 0",
                      borderBottom:
                        index !== assignees.length - 1
                          ? darkMode
                            ? "1px solid #374151"
                            : "1px solid #f1f5f9"
                          : "none",
                    }}
                  >
                    {canDeleteAssignees && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedAssigneeIds((prev) =>
                            prev.includes(userKey)
                              ? prev.filter((id) => id !== userKey)
                              : [...prev, userKey]
                          );
                        }}
                        style={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          border: selectedAssigneeIds.includes(userKey)
                            ? "6px solid #2563eb"
                            : "2px solid #cbd5e1",
                          background: "#ffffff",
                          cursor: "pointer",
                        }}
                      />
                    )}

                    <Avatar name={user.name || user.email} />

                    <div>
                      <div style={{ fontWeight: 600, color: textMain, fontSize: "14px" }}>
                        {user.name || "-"}
                      </div>
                      <div style={{ fontSize: "12px", color: textMuted }}>
                        {user.email || "-"}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={{ color: textMuted, fontSize: "13px" }}>No assigned users found.</p>
            )}
          </Card>

          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle
              title="Files"
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              }
              action={
                <button
                  onClick={() => {
                    setActiveTab("files");
                  }}
                  style={smallButtonStyle}
                >
                  + Upload
                </button>
              }
            />
            {attachments.slice(0, 3).map((file, index) => (
              <FileRow
                key={index}
                file={file}
                textMain={textMain}
                textMuted={textMuted}
                selectable={false}
              />
            ))}
            <button
              onClick={() => setActiveTab("files")}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontWeight: 600,
                marginTop: "10px",
                cursor: "pointer"
              }}
            >
              View all files
            </button>
          </Card>
        </div>
      </div>
    );
  };

  const renderFiles = () => {
    const totalBytes = attachments.reduce((sum, file) => {
      const size =
        file?.size_bytes ||
        file?.file_size_bytes ||
        file?.size ||
        file?.file_size ||
        file?.fileSize ||
        0;

      const numericSize =
        typeof size === "number"
          ? size
          : parseFloat(String(size).replace(/[^\d.]/g, "")) * 1024;

      return sum + (Number.isFinite(numericSize) ? numericSize : 0);
    }, 0);

    const totalUsed =
      totalBytes >= 1024 * 1024
        ? `${(totalBytes / (1024 * 1024)).toFixed(2)} MB used`
        : `${(totalBytes / 1024).toFixed(1)} KB used`;

    return (
      <div className="task-details-files-grid" style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "18px" }}>
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle
            title="Files"
            action={
              <button
                onClick={() => setShowFileUploadForm((prev) => !prev)}
                style={smallButtonStyle}
              >
                + File
              </button>
            }
          />

          {showFileUploadForm && (
            <div
              className="task-details-file-upload-grid"
              style={{
                border,
                borderRadius: "12px",
                padding: "14px",
                marginBottom: "18px",
                background: darkMode ? "#111827" : "#ffffff",
              }}
            >
              <fieldset disabled={actionLoading.files} style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: "12px", alignItems: "center" }}>
                <input id="fileName" name="fileName"
                  placeholder="File name"
                  value={fileUploadForm.fileName}
                  onChange={(e) =>
                    setFileUploadForm({
                      ...fileUploadForm,
                      fileName: e.target.value,
                    })
                  }
                  style={{
                    border,
                    borderRadius: "10px",
                    padding: "11px 14px",
                    background: darkMode ? "#111827" : "#ffffff",
                    color: textMain,
                  }}
                />

                <input id="fileInput-1" name="fileInput-1"
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt"
                  onChange={(e) =>
                    setFileUploadForm({
                      ...fileUploadForm,
                      file: e.target.files?.[0] || null,
                    })
                  }
                  style={{
                    border,
                    borderRadius: "10px",
                    padding: "9px",
                    background: darkMode ? "#111827" : "#ffffff",
                    color: textMain,
                  }}
                />

                <button
                  disabled={actionLoading.files}
                  style={{
                    ...smallButtonStyle,
                    background: "#76AB3F",
                    color: "white",
                    opacity: actionLoading.files ? 0.6 : 1,
                    cursor: actionLoading.files ? "not-allowed" : "pointer"
                  }}
                  onClick={async () => {
                    if (actionLoading.files) return;
                    if (!fileUploadForm.file) {
                      alert("Please choose a file.");
                      return;
                    }

                    const selectedFile = fileUploadForm.file;

                    const allowedTypes = [
                      "image/",
                      "application/pdf",
                      "application/msword",
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      "application/vnd.ms-powerpoint",
                      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      "text/plain",
                      "text/csv",
                    ];

                    const isAllowed = allowedTypes.some((type) =>
                      selectedFile.type.startsWith(type)
                    );

                    if (!isAllowed) {
                      alert("Only images, PDF, Word, PPT, Excel, CSV, and TXT files are allowed.");
                      return;
                    }

                    if (!onUploadFile) {
                      alert("Upload function is not connected from parent component.");
                      return;
                    }

                    setActionLoading(prev => ({ ...prev, files: true }));
                    try {
                      const savedFile = await onUploadFile({
                        taskId: task?.id,
                        fileName: fileUploadForm.fileName || selectedFile.name,
                        file: selectedFile,
                      });

                      if (!savedFile) {
                        throw new Error("File upload failed.");
                      }

                      setFileUploadForm({
                        fileName: "",
                        file: null,
                      });

                      setShowFileUploadForm(false);
                    } catch (err) {
                      alert("Failed to upload files: " + (err.message || "Unknown error"));
                    } finally {
                      setActionLoading(prev => ({ ...prev, files: false }));
                    }
                  }}
                >
                  {actionLoading.files ? "Uploading files..." : "Upload"}
                </button>
                <button
                  disabled={actionLoading.files}
                  onClick={() => setShowFileUploadForm(false)}
                  style={{
                    ...smallButtonStyle,
                    background: "#e5e7eb",
                    color: "#374151",
                    opacity: actionLoading.files ? 0.6 : 1,
                    cursor: actionLoading.files ? "not-allowed" : "pointer"
                  }}
                >
                  Cancel
                </button>
              </fieldset>
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", marginBottom: "18px" }}>
            <input id="searchFiles" name="searchFiles"
              placeholder="Search files..."
              value={fileSearch}
              onChange={(e) => setFileSearch(e.target.value)}
              style={{
                flex: 1,
                border,
                borderRadius: "10px",
                padding: "11px 14px",
                background: darkMode ? "#111827" : "#ffffff",
                color: textMain,
              }}
            />

            {["All", "Images", "Documents", "Sheets"].map((type) => (
              <button
                key={type}
                onClick={() => setFileTypeFilter(type)}
                style={{
                  ...filterButtonStyle,
                  background: fileTypeFilter === type ? "#2563eb" : "#ffffff",
                  color: fileTypeFilter === type ? "#ffffff" : "#334155",
                }}
              >
                {type}
              </button>
            ))}
          </div>

          {selectedFileKeys.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
                padding: "10px 12px",
                borderRadius: "10px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <strong style={{ color: "#991b1b" }}>
                {selectedFileKeys.length} file(s) selected
              </strong>

              <button
                onClick={async () => {
                  if (!window.confirm("Delete selected files?")) return;

                  const selectedFiles = attachments.filter((file, index) =>
                    selectedFileKeys.includes(getFileKey(file, index))
                  );

                  if (onDeleteFiles) {
                    const success = await onDeleteFiles(selectedFiles);

                    if (!success) {
                      alert("Failed to delete selected files.");
                      return;
                    }
                  }

                  setLocalFiles((prev) =>
                    prev.filter((file, index) => {
                      const key = getFileKey(file, index);
                      return !selectedFileKeys.includes(key);
                    })
                  );

                  setSelectedFileKeys([]);
                }}
                style={{
                  border: "none",
                  background: "#dc2626",
                  color: "white",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Delete Selected
              </button>
            </div>
          )}

          {filteredFiles.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {filteredFiles.map((file, index) => {
                const key = getFileKey(file, index);

                return (
                  <FileRow
                    key={key}
                    file={file}
                    textMain={textMain}
                    textMuted={textMuted}
                    large
                    selectable
                    checked={selectedFileKeys.includes(key)}
                    onToggle={() => {
                      setSelectedFileKeys((prev) =>
                        prev.includes(key)
                          ? prev.filter((item) => item !== key)
                          : [...prev, key]
                      );
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <p style={{ color: textMuted }}>No files uploaded.</p>
          )}
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle textMain={textMain} title="Storage Summary" />
            <p style={{ color: textMuted, margin: 0 }}>
              {attachments.length} files uploaded
            </p>
            <strong style={{ display: "block", marginTop: "8px", color: textMain }}>
              {attachments.length ? totalUsed : "0 KB used"}
            </strong>
          </Card>

          <Card cardBg={cardBg} border={border} darkMode={darkMode}>
            <SectionTitle textMain={textMain} title="Recent Activity" />
            {attachments.length > 0 ? (
              attachments.slice(0, 4).map((file, index) => (
                <div key={index} style={{ marginBottom: "14px" }}>
                  <strong style={{ color: textMain, fontSize: "13px" }}>
                    {file.name || file.file_name || file.fileName || "Attachment"}
                  </strong>
                  <div style={{ color: textMuted, fontSize: "12px" }}>
                    Uploaded from {file.source || "Task"}
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: textMuted }}>No recent file activity.</p>
            )}
          </Card>
        </div>
      </div>
    );
  };

  const renderExtension = () => {
    if (selectedExtension) {
      const files = Array.isArray(selectedExtension.attachments)
        ? selectedExtension.attachments
        : [];

      return (
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <button
            onClick={() => setSelectedExtension(null)}
            style={smallButtonStyle}
          >
            ← Back to Extension Requests
          </button>

          <h2 style={{ color: textMain, marginTop: "18px" }}>
            {selectedExtension.extension_number || "EXT"}
          </h2>

          <StatusBadge value={selectedExtension.status || "Pending"} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "16px",
              marginTop: "22px",
            }}
          >
            <SmallMeta
              textMain={textMain}
              textMuted={textMuted}
              label="Requested Date"
              value={formatDate(selectedExtension.requested_due_date)}
            />

            <SmallMeta
              textMain={textMain}
              textMuted={textMuted}
              label="Requested By"
              value={getRequestedByName(selectedExtension)}
            />

            <SmallMeta
              textMain={textMain}
              textMuted={textMuted}
              label="Requested On"
              value={formatDate(selectedExtension.created_at)}
            />
          </div>

          <h3 style={{ color: textMain, marginTop: "24px" }}>
            Request Reason
          </h3>
          <p style={{ color: textMuted, lineHeight: 1.7 }}>
            {selectedExtension.reason || "-"}
          </p>

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Request Note
          </h3>
          <p style={{ color: textMuted, lineHeight: 1.7 }}>
            {selectedExtension.request_note || "-"}
          </p>

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Attachments
          </h3>

          {files.length > 0 ? (
            files.map((file, index) => (
              <FileRow
                key={index}
                file={file}
                textMain={textMain}
                textMuted={textMuted}
                selectable={false}
              />
            ))
          ) : (
            <p style={{ color: textMuted }}>No attachments added.</p>
          )}

          {selectedExtension.status === "Pending" &&
            currentUser?.role === "Department Admin" && (
              <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
                <button
                  onClick={() => {
                    onApproveExtension(selectedExtension.id);
                    setSelectedExtension(null);
                  }}
                  style={{
                    background: "#16a34a",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Accept
                </button>

                <button
                  onClick={() => {
                    onRejectExtension(selectedExtension.id);
                    setSelectedExtension(null);
                  }}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Reject
                </button>
              </div>
            )}
        </Card>
      );
    }

    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "18px" }}>
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle textMain={textMain} title="New Extension Request" />

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}>
            <fieldset disabled={actionLoading.extension} style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: "12px" }}>
              <input id="requestedduedate" name="requestedduedate"
                type="date"
                value={extensionForm.requestedDueDate}
                onChange={(e) =>
                  setExtensionForm({
                    ...extensionForm,
                    requestedDueDate: e.target.value,
                  })
                }
                style={{
                  border,
                  borderRadius: "10px",
                  padding: "12px",
                  background: darkMode ? "#111827" : "#ffffff",
                  color: textMain,
                }}
              />

              <textarea
                placeholder="Reason for extension"
                value={extensionForm.reason}
                onChange={(e) =>
                  setExtensionForm({
                    ...extensionForm,
                    reason: e.target.value,
                  })
                }
                style={{
                  border,
                  borderRadius: "10px",
                  padding: "12px",
                  minHeight: "90px",
                  background: darkMode ? "#111827" : "#ffffff",
                  color: textMain,
                  resize: "vertical",
                }}
              />

              <textarea
                placeholder="Request note"
                value={extensionForm.requestNote}
                onChange={(e) =>
                  setExtensionForm({
                    ...extensionForm,
                    requestNote: e.target.value,
                  })
                }
                style={{
                  border,
                  borderRadius: "10px",
                  padding: "12px",
                  minHeight: "90px",
                  background: darkMode ? "#111827" : "#ffffff",
                  color: textMain,
                  resize: "vertical",
                }}
              />

              <input id="fileInput-1" name="fileInput-1"
                type="file"
                multiple
                onChange={(e) =>
                  setExtensionForm({
                    ...extensionForm,
                    files: Array.from(e.target.files || []),
                  })
                }
                style={{
                  border,
                  borderRadius: "10px",
                  padding: "12px",
                  background: darkMode ? "#111827" : "#ffffff",
                  color: textMain,
                }}
              />

              <button
                disabled={actionLoading.extension}
                onClick={async () => {
                  if (actionLoading.extension) return;
                  if (!extensionForm.requestedDueDate) {
                    alert("Please select requested due date.");
                    return;
                  }

                  if (!extensionForm.reason.trim()) {
                    alert("Please enter reason.");
                    return;
                  }

                  if (onAddExtension) {
                    setActionLoading(prev => ({ ...prev, extension: true }));
                    try {
                      const success = await onAddExtension(extensionForm);

                      if (success) {
                        setExtensionForm({
                          requestedDueDate: "",
                          reason: "",
                          requestNote: "",
                          files: [],
                        });
                      }
                    } catch (err) {
                      alert("Failed to request extension: " + (err.message || "Unknown error"));
                    } finally {
                      setActionLoading(prev => ({ ...prev, extension: false }));
                    }
                  }
                }}
                style={{
                  ...smallButtonStyle,
                  background: "#76AB3F",
                  color: "white",
                  width: "180px",
                  opacity: actionLoading.extension ? 0.6 : 1,
                  cursor: actionLoading.extension ? "not-allowed" : "pointer"
                }}
              >
                {actionLoading.extension ? "Submitting request..." : "Submit Request"}
              </button>
            </fieldset>
          </div>
        </Card>

        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle textMain={textMain} title="Extension Requests" />

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {extensionRequests.length > 0 ? (
              extensionRequests.map((request, index) => {
                const extensionNumber = `EXT-${String(index + 1).padStart(4, "0")}`;

                return (
                  <div
                    key={request.id || index}
                    onClick={() =>
                      setSelectedExtension({
                        ...request,
                        extension_number: extensionNumber,
                      })
                    }
                    style={{
                      border,
                      borderRadius: "14px",
                      padding: "14px",
                      background: darkMode ? "#111827" : "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <strong style={{ color: "#2563eb" }}>
                        {extensionNumber}
                      </strong>

                      <StatusBadge value={request.status || "Pending"} />
                    </div>

                    <p style={{ color: textMain }}>
                      Requested due date: {formatDate(request.requested_due_date)}
                    </p>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <SmallMeta
                        textMain={textMain}
                        textMuted={textMuted}
                        label="Requested by"
                        value={getRequestedByName(request)}
                      />

                      <SmallMeta
                        textMain={textMain}
                        textMuted={textMuted}
                        label="Requested on"
                        value={formatDate(request.created_at)}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={{ color: textMuted }}>No extension requests added.</p>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderIssues = () => {
    if (selectedIssue) {
      return (
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <button onClick={() => setSelectedIssue(null)} style={smallButtonStyle}>
            ← Back to Issues
          </button>

          <h2 style={{ color: textMain, marginTop: "18px" }}>
            {selectedIssue.issue_number || "ISSUE"}
          </h2>

          <StatusBadge value={selectedIssue.status || "Open"} />

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            {selectedIssue.title}
          </h3>

          <p style={{ color: textMuted, lineHeight: 1.7 }}>
            {selectedIssue.description || "No description added."}
          </p>

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Attachments
          </h3>

          {Array.isArray(selectedIssue.attachments) &&
          selectedIssue.attachments.length > 0 ? (
            selectedIssue.attachments.map((file, index) => (
              <FileRow
                key={index}
                file={file}
                textMain={textMain}
                textMuted={textMuted}
                selectable={false}
              />
            ))
          ) : (
            <p style={{ color: textMuted }}>No attachments added.</p>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "20px" }}>
            <SmallMeta textMain={textMain} textMuted={textMuted} label="Priority" value={selectedIssue.priority || "Medium"} />
            <SmallMeta textMain={textMain} textMuted={textMuted} label="Reported By" value={selectedIssue.reporter?.name || selectedIssue.reporter?.email || selectedIssue.reported_by || "-"} />
          </div>

          {selectedIssue.status !== "Resolved" && (
            <button
              onClick={async () => {
                if (onAdvanceIssue) {
                  await onAdvanceIssue(selectedIssue);
                  setSelectedIssue(null);
                }
              }}
              style={{
                ...smallButtonStyle,
                background: "#76AB3F",
                color: "white",
                marginTop: "20px",
              }}
            >
              Advance
            </button>
          )}
        </Card>
      );
    }

    return (
      <div className="task-details-issues-grid" style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "18px" }}>
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle
            title="Issues"
            action={
              <button onClick={() => setShowIssueForm(true)} style={smallButtonStyle}>
                + Issue
              </button>
            }
          />

          {showIssueForm && (
            <div style={{ display: "grid", gap: "10px", marginBottom: "16px" }}>
              <fieldset disabled={actionLoading.issue} style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: "10px" }}>
                <input id="issueTitle" name="issueTitle"
                  placeholder="Issue title"
                  value={issueForm.title}
                  onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                  style={{ border, borderRadius: "10px", padding: "12px" }}
                />

                <textarea
                  placeholder="Issue description"
                  value={issueForm.description}
                  onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                  style={{ border, borderRadius: "10px", padding: "12px", minHeight: "70px" }}
                />

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <span style={{ fontWeight: 800, color: textMain }}>Priority:</span>

                  {["Low", "Medium", "High"].map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setIssueForm({ ...issueForm, priority })}
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        border:
                          issueForm.priority === priority
                            ? "3px solid #2563eb"
                            : "1px solid #d1d5db",
                        background:
                          priority === "High"
                            ? "#fee2e2"
                            : priority === "Low"
                            ? "#dcfce7"
                            : "#fef3c7",
                        color:
                          priority === "High"
                            ? "#b91c1c"
                            : priority === "Low"
                            ? "#166534"
                            : "#92400e",
                        fontSize: "10px",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      title={priority}
                    >
                      {priority[0]}
                    </button>
                  ))}

                  <strong style={{ color: textMain }}>{issueForm.priority}</strong>
                </div>

                <input id="fileInput-1" name="fileInput-1"
                  type="file"
                  multiple
                  onChange={(e) =>
                    setIssueForm({
                      ...issueForm,
                      files: Array.from(e.target.files || []),
                    })
                  }
                />

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    disabled={actionLoading.issue}
                    type="button"
                    style={{
                      ...smallButtonStyle,
                      background: "#76AB3F",
                      color: "white",
                      width: "140px",
                      opacity: actionLoading.issue ? 0.6 : 1,
                      cursor: actionLoading.issue ? "not-allowed" : "pointer"
                    }}
                    onClick={async () => {
                      if (actionLoading.issue) return;
                      if (!onAddIssue) {
                        alert("Issue submit function is not connected.");
                        return;
                      }

                      setActionLoading(prev => ({ ...prev, issue: true }));
                      try {
                        const success = await onAddIssue(issueForm);

                        if (success) {
                          setIssueForm({
                            title: "",
                            description: "",
                            priority: "Medium",
                            files: [],
                          });
                          setShowIssueForm(false);
                        }
                      } catch (err) {
                        alert("Failed to submit issue: " + (err.message || "Unknown error"));
                      } finally {
                        setActionLoading(prev => ({ ...prev, issue: false }));
                      }
                    }}
                  >
                    {actionLoading.issue ? "Adding issue..." : "Submit Issue"}
                  </button>
                  <button
                    disabled={actionLoading.issue}
                    type="button"
                    onClick={() => setShowIssueForm(false)}
                    style={{
                      ...smallButtonStyle,
                      background: "#e5e7eb",
                      color: "#374151",
                      opacity: actionLoading.issue ? 0.6 : 1,
                      cursor: actionLoading.issue ? "not-allowed" : "pointer"
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </fieldset>
            </div>
          )}

          <IssueGroup
            title="Open"
            issues={openIssues}
            task={task}
            textMuted={textMuted}
            textMain={textMain}
            onOpenIssue={setSelectedIssue}
            getIssueNumber={getIssueNumber}
          />

          <IssueGroup
            title="In Progress"
            issues={inProgressIssues}
            task={task}
            textMuted={textMuted}
            textMain={textMain}
            onOpenIssue={setSelectedIssue}
            getIssueNumber={getIssueNumber}
          />

          <IssueGroup
            title="Resolved"
            issues={resolvedIssues}
            task={task}
            textMuted={textMuted}
            textMain={textMain}
            onOpenIssue={setSelectedIssue}
            getIssueNumber={getIssueNumber}
          />
        </Card>

        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle textMain={textMain} title="Issue Summary" />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="Open" value={openIssues.length} />
          <div style={{ height: "14px" }} />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="In Progress" value={inProgressIssues.length} />
          <div style={{ height: "14px" }} />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="Resolved" value={resolvedIssues.length} />
        </Card>
      </div>
    );
  };

  const renderUserCases = () => {
    if (selectedUserCase) {
      const testCases = Array.isArray(selectedUserCase.test_cases)
        ? selectedUserCase.test_cases
        : [];

      const files = Array.isArray(selectedUserCase.attachments)
        ? selectedUserCase.attachments
        : [];

      return (
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <button onClick={() => setSelectedUserCase(null)} style={smallButtonStyle}>
            ← Back to User Stories
          </button>

          <h2 style={{ color: textMain, marginTop: "18px" }}>
            {selectedUserCase.user_case_number || "US"}
          </h2>

          <StatusBadge value={selectedUserCase.status || "Pending"} />

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            {selectedUserCase.title}
          </h3>

          <p style={{ color: textMuted, lineHeight: 1.7 }}>
            {selectedUserCase.description || "No description added."}
          </p>

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Acceptance Criteria
          </h3>

          <p style={{ color: textMuted, lineHeight: 1.7 }}>
            {selectedUserCase.acceptance_criteria ||
              selectedUserCase.acceptanceCriteria ||
              "-"}
          </p>

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Test Cases
          </h3>

          {testCases.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {testCases.map((tc, index) => (
                <div
                  key={index}
                  style={{
                    border,
                    borderRadius: "12px",
                    padding: "12px",
                    background: darkMode ? "#111827" : "#ffffff",
                  }}
                >
                  <strong style={{ color: "#2563eb" }}>
                    TC-{String(index + 1).padStart(3, "0")}
                  </strong>
                  <p style={{ color: textMuted, margin: "6px 0 0" }}>
                    {tc.title || tc.text || tc}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: textMuted }}>No test cases added.</p>
          )}

          <h3 style={{ color: textMain, marginTop: "20px" }}>
            Attachments
          </h3>

          {files.length > 0 ? (
            files.map((file, index) => (
              <FileRow
                key={index}
                file={file}
                textMain={textMain}
                textMuted={textMuted}
                selectable={false}
              />
            ))
          ) : (
            <p style={{ color: textMuted }}>No attachments added.</p>
          )}

          <button
            onClick={async () => {
              if (!onAdvanceUserCase) {
                alert("Advance user case function is not connected.");
                return;
              }

              const success = await onAdvanceUserCase(selectedUserCase);

              if (success) {
                setSelectedUserCase(null);
              }
            }}
            style={{
              ...smallButtonStyle,
              background: "#76AB3F",
              color: "white",
              marginTop: "20px",
            }}
          >
            Advance
          </button>
        </Card>
      );
    }

    return (
      <div className="task-details-usercases-grid" style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "18px" }}>
        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle
            title="User Stories"
            action={
              <button onClick={() => setShowUserCaseForm(true)} style={smallButtonStyle}>
                + User Story
              </button>
            }
          />

          {showUserCaseForm && (
            <div style={{ display: "grid", gap: "10px", marginBottom: "16px" }}>
              <fieldset disabled={actionLoading.userStory} style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: "10px" }}>
                <input id="userStoryTitle" name="userStoryTitle"
                  placeholder="User story title"
                  value={userCaseForm.title}
                  onChange={(e) =>
                    setUserCaseForm({ ...userCaseForm, title: e.target.value })
                  }
                  style={{ border, borderRadius: "10px", padding: "12px" }}
                />

                <textarea
                  placeholder="User story description"
                  value={userCaseForm.description}
                  onChange={(e) =>
                    setUserCaseForm({ ...userCaseForm, description: e.target.value })
                  }
                  style={{ border, borderRadius: "10px", padding: "12px", minHeight: "70px" }}
                />

                <textarea
                  placeholder="Acceptance criteria"
                  value={userCaseForm.acceptanceCriteria}
                  onChange={(e) =>
                    setUserCaseForm({
                      ...userCaseForm,
                      acceptanceCriteria: e.target.value,
                    })
                  }
                  style={{ border, borderRadius: "10px", padding: "12px", minHeight: "70px" }}
                />

                <div style={{ position: "relative" }}>
                  <textarea
                    placeholder="Test case 1"
                    value={userCaseForm.testCases[0] || ""}
                    onChange={(e) => {
                      const updated = [...userCaseForm.testCases];
                      updated[0] = e.target.value;
                      setUserCaseForm({ ...userCaseForm, testCases: updated });
                    }}
                    style={{
                      border,
                      borderRadius: "10px",
                      padding: "12px",
                      minHeight: "70px",
                      width: "100%",
                    }}
                  />

                  <button
                    type="button"
                    disabled={actionLoading.userStory}
                    onClick={() =>
                      setUserCaseForm({
                        ...userCaseForm,
                        testCases: [...userCaseForm.testCases, ""],
                      })
                    }
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      border: "none",
                      background: "#2563eb",
                      color: "white",
                      borderRadius: "8px",
                      width: "30px",
                      height: "30px",
                      fontWeight: 900,
                      cursor: actionLoading.userStory ? "not-allowed" : "pointer",
                      opacity: actionLoading.userStory ? 0.6 : 1
                    }}
                  >
                    +
                  </button>
                </div>

                {userCaseForm.testCases.slice(1).map((testCase, index) => (
                  <textarea
                    key={index}
                    placeholder={`Test case ${index + 2}`}
                    value={testCase}
                    onChange={(e) => {
                      const updated = [...userCaseForm.testCases];
                      updated[index + 1] = e.target.value;
                      setUserCaseForm({ ...userCaseForm, testCases: updated });
                    }}
                    style={{
                      border,
                      borderRadius: "10px",
                      padding: "12px",
                      minHeight: "70px",
                      width: "100%",
                    }}
                  />
                ))}

                <input id="fileInput-1" name="fileInput-1"
                  type="file"
                  multiple
                  onChange={(e) =>
                    setUserCaseForm({
                      ...userCaseForm,
                      files: Array.from(e.target.files || []),
                    })
                  }
                />

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    disabled={actionLoading.userStory}
                    style={{
                      ...smallButtonStyle,
                      background: "#76AB3F",
                      color: "white",
                      width: "170px",
                      opacity: actionLoading.userStory ? 0.6 : 1,
                      cursor: actionLoading.userStory ? "not-allowed" : "pointer"
                    }}
                    onClick={async () => {
                      if (actionLoading.userStory) return;
                      if (!onAddUserCase) {
                        alert("User story submit function is not connected.");
                        return;
                      }

                      setActionLoading(prev => ({ ...prev, userStory: true }));
                      try {
                        const success = await onAddUserCase(userCaseForm);

                        if (success) {
                          setUserCaseForm({
                            title: "",
                            description: "",
                            acceptanceCriteria: "",
                            testCases: [""],
                            files: [],
                          });
                          setShowUserCaseForm(false);
                        }
                      } catch (err) {
                        alert("Failed to add user story: " + (err.message || "Unknown error"));
                      } finally {
                        setActionLoading(prev => ({ ...prev, userStory: false }));
                      }
                    }}
                  >
                    {actionLoading.userStory ? "Adding user story..." : "Submit User Story"}
                  </button>
                  <button
                    disabled={actionLoading.userStory}
                    type="button"
                    onClick={() => setShowUserCaseForm(false)}
                    style={{
                      ...smallButtonStyle,
                      background: "#e5e7eb",
                      color: "#374151",
                      opacity: actionLoading.userStory ? 0.6 : 1,
                      cursor: actionLoading.userStory ? "not-allowed" : "pointer"
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </fieldset>
            </div>
          )}

          <UserCaseGroup
            title="Pending User Stories"
            userCases={pendingUserCases}
            textMain={textMain}
            textMuted={textMuted}
            darkMode={darkMode}
            border={border}
            onOpenUserCase={setSelectedUserCase}
            getUserStoryNumber={getUserStoryNumber}
          />

          <UserCaseGroup
            title="Passed User Stories"
            userCases={passedUserCases}
            textMain={textMain}
            textMuted={textMuted}
            darkMode={darkMode}
            border={border}
            onOpenUserCase={setSelectedUserCase}
            getUserStoryNumber={getUserStoryNumber}
          />

          <UserCaseGroup
            title="In Review User Stories"
            userCases={inReviewUserCases}
            textMain={textMain}
            textMuted={textMuted}
            darkMode={darkMode}
            border={border}
            onOpenUserCase={setSelectedUserCase}
            getUserStoryNumber={getUserStoryNumber}
          />
        </Card>

        <Card cardBg={cardBg} border={border} darkMode={darkMode}>
          <SectionTitle textMain={textMain} title="Summary" />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="Total" value={userCases.length} />
          <div style={{ height: "14px" }} />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="Passed" value={completionStats.completed} />
          <div style={{ height: "14px" }} />
          <SmallMeta textMain={textMain} textMuted={textMuted} label="In Review" value={completionStats.inProgress} />
        </Card>
      </div>
    );
  };

  const teamName =
    task?.teams?.name ||
    task?.team?.name ||
    task?.team_name ||
    "N/A";

  const subTeamName =
    task?.sub_teams?.name ||
    task?.sub_team?.name ||
    task?.sub_team_name ||
    "N/A";

  return (
    <div style={{ minHeight: "100vh", background: pageBg }}>
      <div style={{ padding: "28px 38px" }}>
        <button
          onClick={onBack}
          style={{
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            color: "#0f172a",
            borderRadius: "6px",
            padding: "6px 14px",
            fontSize: "12px",
            fontWeight: 500,
            cursor: "pointer",
            marginBottom: "16px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
          }}
        >
          ← Back
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "22px"
          }}
        >
          <h2 style={{ color: textMain, margin: 0, fontSize: "24px", fontWeight: 600 }}>
            {task?.title || "Untitled Task"}
          </h2>

          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowTaskMenu((prev) => !prev)}
              style={topButtonStyle}
            >
              ...
            </button>

            {showTaskMenu && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "46px",
                  width: "150px",
                  background: cardBg,
                  border,
                  borderRadius: "12px",
                  boxShadow: darkMode ? "none" : "0 10px 28px rgba(15, 23, 42, 0.14)",
                  padding: "8px",
                  zIndex: 20,
                }}
              >
                <button
                  onClick={() => {
                    setShowTaskMenu(false);
                    onEdit && onEdit();
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    padding: "10px 12px",
                    textAlign: "left",
                    fontWeight: 500,
                    color: textMain,
                    cursor: "pointer",
                    borderRadius: "8px",
                  }}
                >
                  Edit Task
                </button>
                {canDeleteTask && (
                  <button
                    onClick={() => {
                      setShowTaskMenu(false);
                      setShowDeleteModal(true);
                    }}
                    style={{
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      padding: "10px 12px",
                      textAlign: "left",
                      fontWeight: 500,
                      color: "#dc2626",
                      cursor: "pointer",
                      borderRadius: "8px",
                      marginTop: "4px"
                    }}
                  >
                    Delete Task
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {showDeleteModal && (
          <DeleteModal
            task={task}
            darkMode={darkMode}
            onClose={() => setShowDeleteModal(false)}
            onDelete={async (taskId, deleteSeries) => {
              if (onDeleteTask) {
                const success = await onDeleteTask(taskId, deleteSeries);
                if (success !== false) {
                  onBack && onBack();
                } else {
                  alert("Failed to delete task.");
                }
              }
              setShowDeleteModal(false);
            }}
          />
        )}


        <div
          className="task-details-metadata-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "22px",
            maxWidth: "100%"
          }}
        >
          {/* Created by */}
          <MiniMetadataCard
            label="created by"
            value={getCreatedByName(task)}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
            iconBg="#eff6ff"
            iconColor="#3b82f6"
            darkMode={darkMode}
          />

          {/* Assignees */}
          <MiniMetadataCard
            label="assignees"
            value={renderAssigneesValue()}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
            iconBg="#f0fdf4"
            iconColor="#22c55e"
            darkMode={darkMode}
          />

          {/* Team */}
          <MiniMetadataCard
            label="team"
            value={teamName}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            iconBg="#faf5ff"
            iconColor="#a855f7"
            darkMode={darkMode}
          />

          {/* Sub Team */}
          <MiniMetadataCard
            label="sub team"
            value={subTeamName}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            }
            iconBg="#fdf4ff"
            iconColor="#c026d3"
            darkMode={darkMode}
          />

          {/* Priority */}
          <MiniMetadataCard
            label="priority"
            value={task?.priority || "Medium"}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
            }
            iconBg="#fff7ed"
            iconColor="#f97316"
            darkMode={darkMode}
          />

          {/* Status */}
          <MiniMetadataCard
            label="status"
            value={<StatusBadge value={task?.status || "Todo"} />}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
            iconBg="#eff6ff"
            iconColor="#3b82f6"
            darkMode={darkMode}
          />

          {/* Start date */}
          <MiniMetadataCard
            label="start date"
            value={formatDate(task?.start_date || task?.repeat_start_date)}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            iconBg="#f0fdf4"
            iconColor="#22c55e"
            darkMode={darkMode}
          />

          {/* Due date */}
          <MiniMetadataCard
            label="due date"
            value={formatDate(task?.due_date)}
            icon={
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            iconBg="#f0fdf4"
            iconColor="#22c55e"
            darkMode={darkMode}
          />

          {/* Repeat (only for loop tasks) */}
          {task?.task_type === "loop" && (
            <MiniMetadataCard
              label="repeat"
              value={getRepeatText()}
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.5" />
                </svg>
              }
              iconBg="#eff6ff"
              iconColor="#3b82f6"
              darkMode={darkMode}
            />
          )}

          {/* Duration (only for loop tasks) */}
          {task?.task_type === "loop" && (
            <MiniMetadataCard
              label="duration"
              value={getLoopDurationText()}
              icon={
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              iconBg="#eff6ff"
              iconColor="#3b82f6"
              darkMode={darkMode}
            />
          )}
        </div>

        <div
          className="task-details-tabs-row"
          style={{
            display: "flex",
            gap: "26px",
            borderBottom: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
            marginBottom: "18px"
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: "transparent",
                border: "none",
                borderBottom:
                  activeTab === tab.key ? "3px solid #2563eb" : "3px solid transparent",
                color: activeTab === tab.key ? "#2563eb" : textMuted,
                fontWeight: 900,
                padding: "14px 4px",
                cursor: "pointer"
              }}
            >
              {tab.label}
              {typeof tab.count === "number" && (
                <span
                  style={{
                    marginLeft: "6px",
                    background: "#e5e7eb",
                    borderRadius: "999px",
                    padding: "2px 7px",
                    fontSize: "11px",
                    color: "#475569"
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "overview" && renderOverview()}
        {activeTab === "files" && renderFiles()}
        {activeTab === "extension" && renderExtension()}
        {activeTab === "issues" && renderIssues()}
        {activeTab === "userCases" && renderUserCases()}
      </div>
    </div>
  );
}

const smallButtonStyle = {
  border: "none",
  background: "rgba(37, 99, 235, 0.08)",
  color: "#2563eb",
  borderRadius: "8px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease"
};

const topButtonStyle = {
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: "#0f172a",
  borderRadius: "8px",
  padding: "8px 14px",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
};

const filterButtonStyle = {
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  color: "#334155",
  borderRadius: "10px",
  padding: "10px 12px",
  fontWeight: 600,
  cursor: "pointer"
};

function Avatar({ name }) {
  const initials = (name || "U")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        width: "34px",
        height: "34px",
        borderRadius: "50%",
        background: "#76AB3F",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 500,
        fontSize: "12px"
      }}
    >
      {initials}
    </div>
  );
}

function FileRow({
  file,
  textMain,
  textMuted,
  large = false,
  selectable = false,
  checked = false,
  onToggle,
}) {
  const fileName =
    file.name ||
    file.file_name ||
    file.fileName ||
    file.original_filename ||
    "Attachment";

  const fileUrl =
    file.url ||
    file.publicUrl ||
    file.public_url ||
    file.file_url ||
    file.download_url;

  const rawSize =
    file.size ||
    file.file_size ||
    file.fileSize ||
    file.size_bytes ||
    file.file_size_bytes ||
    0;

  const fileSize =
    typeof rawSize === "number"
      ? rawSize >= 1024 * 1024
        ? `${(rawSize / (1024 * 1024)).toFixed(2)} MB`
        : `${(rawSize / 1024).toFixed(1)} KB`
      : rawSize || "Unknown size";

  const openFile = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      onClick={openFile}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: large ? "14px" : "12px",
        marginBottom: large ? 0 : "10px",
        cursor: fileUrl ? "pointer" : "default",
        background: "#ffffff",
        transition: "all 0.2s ease"
      }}
    >
      {selectable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle && onToggle();
          }}
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            border: checked ? "6px solid #2563eb" : "2px solid #cbd5e1",
            background: "#ffffff",
            cursor: "pointer",
            flexShrink: 0
          }}
        />
      )}

      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: "#2563eb", flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>

      <div style={{ minWidth: 0, flex: 1 }}>
        <strong style={{ color: "#2563eb", fontWeight: 500, fontSize: "13px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fileName}
        </strong>

        <div style={{ fontSize: "12px", color: textMuted, marginTop: "2px" }}>
          {fileSize}
          {file.source ? ` • ${file.source}` : ""}
        </div>

        {file.sourceTitle && (
          <div style={{ fontSize: "11px", color: textMuted, marginTop: "2px" }}>
            {file.sourceTitle}
          </div>
        )}
      </div>
    </div>
  );
}


function ExtensionCard({
  requests = [],
  task,
  users = [],
  textMuted,
  textMain,
  formatDate,
  currentUser,
  onApproveExtension,
  onRejectExtension,
  onOpenExtension,
}) {
  if (!requests.length) {
    return <p style={{ color: textMuted }}>No extension requests added.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {requests.map((request, index) => (
        <div
          key={request.id || index}
          onClick={() => {
            const extensionNumber = `EXT-${String(index + 1).padStart(4, "0")}`;
            onOpenExtension && onOpenExtension(request, extensionNumber);
          }}
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "14px",
            cursor: "pointer",
            transition: "0.2s",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong style={{ color: "#2563eb" }}>
              EXT-{String(index + 1).padStart(4, "0")}
            </strong>

            <span
              style={{
                background: "#e5e7eb",
                borderRadius: "999px",
                padding: "4px 9px",
                fontSize: "12px",
                fontWeight: 800,
              }}
            >
              {request.status || "Pending"}
            </span>
          </div>

          <p style={{ color: textMain }}>
            Requested due date: {formatDate(request.requested_due_date)}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div>
              <div style={{ color: textMuted, fontSize: "12px" }}>Requested by</div>
              <strong style={{ color: textMain }}>
                {request.requester?.name ||
                 request.requester?.email ||
                 users.find((u) => u.id === request.requested_by)?.name ||
                 "-"}
              </strong>
            </div>

            <div>
              <div style={{ color: textMuted, fontSize: "12px" }}>Requested on</div>
              <strong style={{ color: textMain }}>
                {formatDate(request.created_at)}
              </strong>
            </div>
          </div>

          {request.status === "Pending" &&
            currentUser?.role === "Department Admin" && (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "14px",
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onApproveExtension(request.id);
                  }}
                  style={{
                    background: "#16a34a",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Accept
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRejectExtension(request.id);
                  }}
                  style={{
                    background: "#dc2626",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Reject
                </button>
              </div>
            )}
        </div>
      ))}
    </div>
  );
}

function IssueGroup({ title, issues, task, textMuted, textMain, onOpenIssue, getIssueNumber }) {
  return (
    <div style={{ marginBottom: "22px" }}>
      <h3 style={{ color: textMain, borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" }}>
        {title} <span style={{ color: textMuted }}>({issues.length})</span>
      </h3>

      <IssueList
        issues={issues}
        task={task}
        textMuted={textMuted}
        textMain={textMain}
        full
        onOpenIssue={onOpenIssue}
        getIssueNumber={getIssueNumber}
      />
    </div>
  );
}

function IssueList({ issues = [], task, textMuted, textMain, full = false, onOpenIssue, getIssueNumber }) {
  if (!issues.length) {
    return <p style={{ color: textMuted }}>No issues added.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {issues.slice(0, full ? issues.length : 2).map((issue, index) => {
        const issueNumber = getIssueNumber
          ? getIssueNumber(issue)
          : issue.issue_number || `ISSUE_${String(index + 1).padStart(3, "0")}`;

        return (
          <div
            key={issue.id}
            onClick={() =>
              onOpenIssue &&
              onOpenIssue({
                ...issue,
                issue_number: issueNumber,
              })
            }
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              padding: "14px",
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong style={{ color: "#2563eb" }}>{issueNumber}</strong>
            </div>

            <p style={{ color: textMain }}>{issue.title}</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <div style={{ color: textMuted, fontSize: "11px", fontWeight: 400 }}>Priority</div>
                <strong style={{ color: textMain, fontWeight: 500, fontSize: "13px" }}>{issue.priority || "Medium"}</strong>
              </div>

              <div>
                <div style={{ color: textMuted, fontSize: "11px", fontWeight: 400 }}>Reported by</div>
                <strong style={{ color: textMain, fontWeight: 500, fontSize: "13px" }}>
                  {issue.reporter?.name || issue.reporter?.email || issue.reported_by || "-"}
                </strong>
              </div>
            </div>


          </div>
        );
      })}
    </div>
  );
}

function UserCaseGroup({
  title,
  userCases = [],
  textMain,
  textMuted,
  darkMode,
  border,
  onOpenUserCase,
  getUserStoryNumber,
}) {
  return (
    <div style={{ marginBottom: "22px" }}>
      <h3 style={{ color: textMain, borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" }}>
        {title} <span style={{ color: textMuted }}>({userCases.length})</span>
      </h3>

      {userCases.length === 0 ? (
        <p style={{ color: textMuted }}>No user stories added.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {userCases.map((uc, index) => {
            const userCaseNumber = getUserStoryNumber
              ? getUserStoryNumber(uc)
              : uc.user_case_number || `US-${String(index + 1).padStart(3, "0")}`;

            return (
              <div
                key={uc.id || index}
                onClick={() =>
                  onOpenUserCase({
                    ...uc,
                    user_case_number: userCaseNumber,
                  })
                }
                style={{
                  border,
                  borderRadius: "14px",
                  padding: "14px",
                  background: darkMode ? "#111827" : "#ffffff",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong style={{ color: "#2563eb" }}>{userCaseNumber}</strong>
                </div>

                <p style={{ color: textMain }}>{uc.title || "-"}</p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <SmallMeta textMain={textMain} textMuted={textMuted} label="Status" value={uc.status || "Pending"} />
                  <SmallMeta textMain={textMain} textMuted={textMuted} label="Test Cases" value={uc.test_cases_count || 0} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserCaseMiniList({
  userCases,
  textMain,
  textMuted,
  getUserStoryNumber,
  onOpenUserCase,
}) {
  if (!userCases.length) {
    return <p style={{ color: textMuted, fontSize: "13px" }}>No user stories added.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {userCases.slice(0, 3).map((uc, index) => {
        const userCaseNumber = getUserStoryNumber
          ? getUserStoryNumber(uc)
          : `US-${String(index + 1).padStart(3, "0")}`;

        return (
          <div
            key={uc.id || index}
            onClick={() =>
              onOpenUserCase &&
              onOpenUserCase({
                ...uc,
                user_case_number: userCaseNumber,
              })
            }
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "12px",
              cursor: "pointer",
            }}
          >
            <div>
              <strong style={{ color: "#2563eb", fontWeight: 500 }}>{userCaseNumber}</strong>
              <span style={{ marginLeft: "10px", color: textMain, fontSize: "13px" }}>
                {uc.title}
              </span>
            </div>

            <StatusBadge value={uc.status || "Pending"} />
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ value }) {
  const isCompleted = value === "Completed" || value === "Passed";
  const isInProgress = value === "In Progress" || value === "In Review";
  
  let bg = "#e5e7eb";
  let color = "#475569";
  
  if (isCompleted) {
    bg = "#f0fdf4";
    color = "#16a34a";
  } else if (isInProgress) {
    bg = "#fff7ed";
    color = "#ea580c";
  } else if (value === "Todo" || value === "To Do" || value === "Open") {
    bg = "#eff6ff";
    color = "#2563eb";
  }

  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 500,
        background: bg,
        color: color,
        display: "inline-block"
      }}
    >
      {value || "Pending"}
    </span>
  );
}

function SmallMeta({ label, value, textMain, textMuted }) {
  return (
    <div>
      <div style={{ fontSize: "11px", color: textMuted, fontWeight: 400 }}>{label}</div>
      <div style={{ fontSize: "13px", color: textMain, fontWeight: 500, marginTop: "4px" }}>
        {value === 0 ? 0 : value || "-"}
      </div>
    </div>
  );
}

function MiniMetadataCard({ label, value, icon, iconBg, iconColor, darkMode }) {
  const cardBg = darkMode ? "#1f2937" : "#ffffff";
  const border = darkMode ? "1px solid #374151" : "1px solid #e2e8f0";
  const textMain = darkMode ? "#f9fafb" : "#0f172a";
  const textMuted = darkMode ? "#9ca3af" : "#64748b";

  return (
    <div
      title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: cardBg,
        border,
        borderRadius: "12px",
        padding: "8px 12px",
        minHeight: "80px",
        overflow: "hidden",
        boxShadow: darkMode ? "none" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        boxSizing: "border-box"
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: iconBg,
          color: iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0
        }}
      >
        {/* Scale the SVG to fit the smaller container */}
        <div style={{ transform: "scale(0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, justifyContent: "center" }}>
        <span style={{ fontSize: "11px", color: textMuted, fontWeight: 500, marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {label}
        </span>
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: textMain,
            wordBreak: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.2
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function Card({ children, style = {}, cardBg, border, darkMode }) {
  return (
    <div
      style={{
        background: cardBg,
        border,
        borderRadius: "16px",
        padding: "20px",
        boxShadow: darkMode ? "none" : "0 8px 24px rgba(15, 23, 42, 0.04)",
        ...style
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ title, action, textMain, icon }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {icon && <span style={{ color: "#64748b", display: "flex", alignItems: "center" }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: textMain }}>
          {title}
        </h3>
      </div>
      {action}
    </div>
  );
}

function DeleteModal({ task, onClose, onDelete, darkMode }) {
  const isLoop = task?.is_loop_occurrence || task?.task_type === "loop";
  
  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 9999 }}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 400 }}
      >
        <div className="modal-header">
          <h3>{isLoop ? "Delete Loop Task" : "Confirm Delete"}</h3>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div>
          {isLoop ? (
            <>
              <p>
                Do you want to delete only this occurrence or the entire loop series?
              </p>
              <div className="modal-actions-row" style={{ marginTop: 24 }}>
                <button className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button 
                  className="btn-danger" 
                  onClick={() => onDelete(task.id, false)}
                >
                  Delete This Task Only
                </button>
                <button 
                  className="btn-danger" 
                  onClick={() => onDelete(task.id, true)}
                >
                  Delete Whole Loop Series
                </button>
              </div>
            </>
          ) : (
            <>
              <p>
                Are you sure you want to delete task{" "}
                <strong>{task.title}</strong>?
              </p>

              <p style={{ fontSize: 13, marginTop: 8 }}>
                This action will move the task to the recycle bin.
              </p>

              <div className="modal-actions-row">
                <button className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>

                <button className="btn-danger" onClick={() => onDelete(task.id, false)}>
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskDetailsPage;